/**
 * Displays a select control to decide how to target assets in the inventory table for update.
 */
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { TARGET_ACTION } from '@kentik/ui-shared/labels/lib';
import { Select } from 'core/components';
import AssetTagInventoryCollection from 'app/stores/assetTags/AssetTagInventoryCollection';

interface Props {
  collection: AssetTagInventoryCollection;
  tagId: string;
}

function TargetAssetSelector({ collection, tagId }: Props) {
  const initialSelectedValue = collection.initTargetAsset({ tagId });
  const [selectedValue, setSelectedValue] = useState(initialSelectedValue);

  useEffect(() => {
    setSelectedValue(initialSelectedValue);
  }, [initialSelectedValue]);

  /**
   * When targeting 'all' assets, new tag values are created for all assets in the group without a value.
   * When targeting to 'keep as is', all the new values are purged from the group.
   */
  const onChange = (value: string) => {
    collection.updateGroupAssets({ value, tagId });
    setSelectedValue(value);
  };

  /**
   * The selector is disabled when all items in the group are already tagged with actual values (no new values)
   */
  const disabled = collection.tagAssetIsDisabled({ tagId });

  return (
    <Select
      options={[
        { value: TARGET_ACTION.KEEP.value, label: TARGET_ACTION.KEEP.label },
        { value: TARGET_ACTION.ALL.value, label: TARGET_ACTION.ALL.label }
      ]}
      onChange={onChange}
      values={[selectedValue]}
      disabled={disabled}
    />
  );
}

export default observer(TargetAssetSelector);
